<template>
  <div id="deal-page" ref="deal-page" :class="{ 'deal-page--giveaway': hasGiveaway }">
    <!-- Custom full-screen pass layouts (ENG-2223) — the backend specifies the
         layout per pass (deal.pass_layout), resolved through PASS_LAYOUTS.
         Rendered directly, NOT through the module system, with the app chrome
         suppressed. Each layout gates itself via its own store has-getter. -->
    <component :is="passLayout.component" v-if="passLayout && passLayoutActive" />
    <template v-else>
      <module-renderer :moduleData="dealModuleData" />
      <Bingo v-if="hasBingo" />
    </template>
  </div>
</template>

<script>
import { ref, provide, onMounted, onBeforeUnmount } from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import { IMAGE_SHOWCASE_OPACITY_KEY } from '@/components/modules/ImageShowcase/constants.js';
import Bingo from '@/components/modules/Bingo/index.vue';
import Giveaway from '@/components/passLayouts/Giveaway/index.vue';

// Custom pass layouts by name (ENG-2223). The backend declares a deal's
// layout via pass_settings->pass_layout (payload: deal.pass_layout); a pass
// type may offer several layouts in the future. Each entry carries the store
// getter that says the layout's data is ready to render.
const PASS_LAYOUTS = {
  giveaway: { component: Giveaway, activeGetter: 'giveaway/hasGiveaway' }
};

const DUMMY_BINGO = {
  grid_size: 5,
  board_mode: 'places',
  multi_win: false,
  bingos_achieved: 0,
  is_blackout: false,
  grid: [
    [
      { type: 'venue', venue_id: 10, marked: false },
      { type: 'venue', venue_id: 11, marked: true },
      { type: 'venue', venue_id: 12, marked: false },
      { type: 'venue', venue_id: 13, marked: false },
      { type: 'venue', venue_id: 14, marked: false }
    ],
    [
      { type: 'venue', venue_id: 15, marked: false },
      { type: 'venue', venue_id: 16, marked: false },
      { type: 'venue', venue_id: 17, marked: false },
      { type: 'venue', venue_id: 18, marked: false },
      { type: 'venue', venue_id: 19, marked: false }
    ],
    [
      { type: 'venue', venue_id: 20, marked: false },
      { type: 'venue', venue_id: 21, marked: false },
      { type: 'free', marked: true },
      { type: 'venue', venue_id: 22, marked: false },
      { type: 'venue', venue_id: 23, marked: false }
    ],
    [
      { type: 'venue', venue_id: 24, marked: false },
      { type: 'venue', venue_id: 25, marked: false },
      { type: 'venue', venue_id: 26, marked: false },
      { type: 'venue', venue_id: 27, marked: false },
      { type: 'venue', venue_id: 28, marked: false }
    ],
    [
      { type: 'venue', venue_id: 29, marked: false },
      { type: 'venue', venue_id: 30, marked: false },
      { type: 'venue', venue_id: 31, marked: false },
      { type: 'venue', venue_id: 32, marked: false },
      { type: 'venue', venue_id: 33, marked: false }
    ]
  ],
  prizes: [
    { type: 'first_bingo', product_id: 999, claimed_at: null },
    { type: 'blackout', product_id: 1000, claimed_at: null }
  ]
};

export default {
  name: 'Deal',
  components: { Bingo, Giveaway },
  data() {
    return {
      loading: true,
      moduleData: []
    };
  },
  setup() {
    const imageShowcaseOpacity = ref(1);
    provide(IMAGE_SHOWCASE_OPACITY_KEY, imageShowcaseOpacity);

    let rafId = null;
    function updateScrollOpacity() {
      const top = typeof window !== 'undefined' ? window.scrollY : 0;
      const imageHeight = Math.min(240, window.innerWidth / 2);
      const opacity = Math.max(0, Math.min(1, 1 - (top / imageHeight) * 2));
      imageShowcaseOpacity.value = opacity;
    }
    function onScroll() {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        updateScrollOpacity();
        rafId = null;
      });
    }

    onMounted(() => {
      updateScrollOpacity();
      window.addEventListener('scroll', onScroll, { passive: true });
    });
    onBeforeUnmount(() => {
      window.removeEventListener('scroll', onScroll);
    });

    return { imageShowcaseOpacity };
  },
  computed: {
    ...mapGetters('assignments', { findAssignments: 'find' }),
    ...mapGetters('bingo', ['hasBingo']),
    ...mapGetters('deals', { findDeal: 'find' }),
    ...mapGetters('giveaway', ['hasGiveaway', 'giveawayDealId']),
    ...mapState(['offer']),
    passLayoutName() {
      const deal = this.findDeal(parseInt(this.$route.params.dealId, 10));
      return deal?.pass_layout || null;
    },
    passLayout() {
      return PASS_LAYOUTS[this.passLayoutName] || null;
    },
    passLayoutActive() {
      return !!(this.passLayout && this.$store.getters[this.passLayout.activeGetter]);
    },
    imageShowcaseIdx() {
      return this.moduleData.findIndex(
        module => module.type === 'ImageShowcase'
      );
    },
    dealModuleData() {
      if (!this.hasBingo) return this.moduleData;
      return this.moduleData.filter(m => m.type !== 'DashboardVenueList');
    }
  },
  methods: {
    ...mapActions('assignments', ['getAssignments']),
    ...mapActions('bingo', ['addBingo']),
    ...mapActions('giveaway', { activateGiveaway: 'activate', resetGiveaway: 'reset' })
  },
  beforeUnmount() {
    // Leaving the deal must fully release the custom layout (restores app chrome).
    if (this.hasGiveaway) {
      this.resetGiveaway();
    }
  },
  async created() {
    // Activate the custom layout BEFORE the awaited assignments fetch — the
    // live payload arrived with the initial pass data and the mock needs no
    // fetch at all. Doing it synchronously means the first render is already
    // the custom layout (no flash of the regular pass + app chrome).
    if (this.passLayoutName === 'giveaway') {
      this.activateGiveaway();
    } else if (this.hasGiveaway) {
      this.resetGiveaway();
    }

    await this.getAssignments({
      assignmentId: parseInt(this.$route.params.dealId, 10),
      offerId: this.offer.id,
      type: 'deal'
    });
    if (this.$route.query.testBingo === '1') {
      this.addBingo(DUMMY_BINGO);
    }

    this.loading = false;

    const { dealId: assignmentId } = this.$route.params;
    const assignment = this.findAssignments(assignmentId);
    this.moduleData = assignment.modules || [];
  }
};
</script>

<style lang="scss" scoped>
#deal-page {
  min-height: 100%;
  padding-top: min(240px, 100vw / 2);
}

// The Giveaway pass renders its own hero inline, so it doesn't need the space
// reserved for the deal's fixed showcase hero. ID-level specificity is required
// to override the #deal-page padding-top above.
#deal-page.deal-page--giveaway {
  padding-top: 0;
}
</style>
